import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SiteFooter } from "./SiteFooter";
import { FeedOutageBanner } from "@/components/FeedOutageBanner";
import { isNativeApp } from "@/lib/platform";

const NAV = [
  { to: "/about", label: "About" },
  { to: "/transparency", label: "Transparency" },
  { to: "/glossary", label: "Terms explained" },
  { to: "/markets", label: "Live markets" },
  { to: "/feed-health", label: "Feed health" },
  { to: "/partners", label: "Investment Partners" },
  { to: "/pricing", label: "Pricing", nativeLabel: "Plans" },
  { to: "/contact", label: "Contact" },
  { to: "/install", label: "Download app" },
];

export const PublicShell = ({ children, title, subtitle, wide = false, updated }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#070A0F] text-slate-100 flex flex-col grain" data-testid="public-shell">
      <header className="sticky top-0 z-40 apx-glass border-b border-slate-800/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center gap-6">
          <Link to="/" data-testid="public-logo" className="shrink-0"><img src="/brand/evidex-logo-notag.jpg" alt="EV-IDEX" className="h-10 rounded-md" /></Link>
          <nav className="hidden md:flex items-center gap-1 ml-2" data-testid="public-nav">
            {NAV.map((n) => ({ ...n, label: isNativeApp() && n.nativeLabel ? n.nativeLabel : n.label })).map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => `px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "text-cyan-200 bg-cyan-500/10" : "text-slate-300 hover:text-white hover:bg-slate-800/60"}`}>{n.label}</NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {user ? (
              <Link to="/dashboard" data-testid="public-open-app" className="btn-primary rounded-xl px-4 py-2 text-sm font-medium">Open app</Link>
            ) : (
              <>
                <Link to="/login" data-testid="public-signin" className="btn-ghost rounded-xl px-4 py-2 text-sm hidden sm:inline-flex">Sign in</Link>
                <Link to="/signup" data-testid="public-signup" className="btn-primary rounded-xl px-4 py-2 text-sm font-medium">Start free</Link>
              </>
            )}
            <button type="button" onClick={() => setOpen((o) => !o)} className="md:hidden btn-ghost rounded-lg p-2" aria-label="Menu" data-testid="public-menu-btn">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          </div>
        </div>
        {open && (
          <nav className="md:hidden border-t border-slate-800/70 px-4 py-3 flex flex-col gap-1" data-testid="public-mobile-nav">
            {NAV.map((n) => <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm text-slate-200 hover:bg-slate-800/60">{isNativeApp() && n.nativeLabel ? n.nativeLabel : n.label}</NavLink>)}
            {!user && <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm text-slate-200 hover:bg-slate-800/60">Sign in</Link>}
          </nav>
        )}
      </header>
      <FeedOutageBanner />

      <main className="flex-1">
        {title && (
          <div className={`${wide ? "max-w-6xl" : "max-w-3xl"} mx-auto px-4 md:px-6 pt-12 pb-6`}>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">{title}</h1>
            {subtitle && <p className="text-slate-400 mt-3 text-base md:text-lg max-w-2xl leading-relaxed">{subtitle}</p>}
            {updated && <p className="text-xs text-slate-500 mt-3">Last updated {updated}</p>}
          </div>
        )}
        <div className={`${wide ? "max-w-6xl" : "max-w-3xl"} mx-auto px-4 md:px-6 pb-16`}>{children}</div>
      </main>
      <SiteFooter />
    </div>
  );
};

export const LegalSection = ({ id, title, children }) => (
  <section id={id} className="apx-card p-5 md:p-6 space-y-3" data-testid={`legal-${id}`}>
    <h2 className="font-display font-semibold text-lg">{title}</h2>
    <div className="text-sm md:text-[15px] text-slate-300 leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:list-inside [&_ul]:space-y-1 [&_strong]:text-slate-100">{children}</div>
  </section>
);
